import { AuthService } from '@/services/authService';

/**
 * Master utilities for checking master user status and permissions
 */

interface TokenPayload {
  userId: string | number;
  email: string;
  userName: string;
  is_master?: boolean;
  client_id?: number;
  role_id?: number;
  sd_org_id?: number | null;
}

/**
 * School IDs that have WhatsApp features enabled (AiSensy & WATI)
 * Loaded from environment variable VITE_WHATSAPP_ENABLED_SCHOOL_IDS
 */
const getWhatsAppEnabledSchoolIds = (): number[] => {
  try {
    const envValue = import.meta.env.VITE_WHATSAPP_ENABLED_SCHOOL_IDS || '';
    if (!envValue) return [];
    return envValue.split(',').map((id: string) => parseInt(id.trim(), 10)).filter((id: number) => !isNaN(id));
  } catch {
    return [];
  }
};

/**
 * Check if current user is logged in as master
 */
export const isMasterUser = (): boolean => {
  try {
    const token = AuthService.getToken();
    if (!token) return false;

    // Decode JWT token (split by . and decode middle part)
    const payload = JSON.parse(atob(token.split('.')[1])) as TokenPayload;
    return payload.is_master === true;
  } catch (error) {
    console.error('Error checking master status:', error);
    return false;
  }
};

/**
 * Get master user info from token
 */
export const getMasterInfo = (): TokenPayload | null => {
  try {
    const token = AuthService.getToken();
    if (!token) return null;

    const payload = JSON.parse(atob(token.split('.')[1])) as TokenPayload;
    return payload.is_master ? payload : null;
  } catch (error) {
    console.error('Error getting master info:', error);
    return null;
  }
};

/**
 * Check if a feature should be visible (master-only)
 */
export const canViewMasterFeature = (_featureName?: string): boolean => {
  // For now, just check if user is master
  // Can be extended to check specific feature permissions
  return isMasterUser();
};

/**
 * Check if user can access WATI/WhatsApp integration features
 * Allowed for: master users OR client_id 9 OR school ID in allowed list
 */
export const canAccessWatiFeatures = (): boolean => {
  try {
    const token = AuthService.getToken();
    if (!token) return false;

    const payload = JSON.parse(atob(token.split('.')[1])) as TokenPayload;
    const enabledSchoolIds = getWhatsAppEnabledSchoolIds();

    // Allow if master OR client_id is 9 OR school ID is in allowed list
    return payload.is_master === true ||
           payload.client_id === 9 ||
           (payload.sd_org_id != null && enabledSchoolIds.includes(payload.sd_org_id));
  } catch (error) {
    console.error('Error checking WATI access:', error);
    return false;
  }
};

/**
 * Check if user can access AiSensy integration features
 * Allowed for: master users OR school ID in allowed list
 */
export const canAccessAiSensyFeatures = (): boolean => {
  try {
    const token = AuthService.getToken();
    if (!token) return false;

    const payload = JSON.parse(atob(token.split('.')[1])) as TokenPayload;
    const enabledSchoolIds = getWhatsAppEnabledSchoolIds();

    // Allow if master OR school ID is in allowed list
    return payload.is_master === true ||
           (payload.sd_org_id != null && enabledSchoolIds.includes(payload.sd_org_id));
  } catch (error) {
    console.error('Error checking AiSensy access:', error);
    return false;
  }
};
